/**
 * Documents api context behavior for ERP Accelerator.
 */

"use client";

import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { startLoading, stopLoading } from '@/components/Common/LoadingBar';
import {
  archiveProject as apiArchiveProject,
  unarchiveProject as apiUnarchiveProject,
  deleteProject as apiDeleteProject,
  batchArchiveProjects as apiBatchArchiveProjects,
  batchUnarchiveProjects as apiBatchUnarchiveProjects,
  batchDeleteProjects as apiBatchDeleteProjects,
  copyProject as apiCopyProject
} from './ProjectManagementApi';

interface NavItem {
  id: string;
  name: string;
  description: string;
  transaction_code?: string;
  item_type: 'folder' | 'transaction' | 'documentation' | 'file';
  parent_id?: string | null;
  display_order?: number;
  icon?: string | null;
  children?: NavItem[];
  has_recordings?: boolean;
  has_transcripts?: boolean;
  has_summaries?: boolean;
}

/**
 * Describes transcription job status value data.
 */
export type TranscriptionJobStatusValue = 'pending' | 'processing' | 'in_progress' | 'queued' | 'completed' | 'failed' | 'timeout';

/**
 * Describes transcription job status response data.
 */
export interface TranscriptionJobStatusResponse {
  status: TranscriptionJobStatusValue | string;
  job_id?: string;
  jobId?: string;
  recording_id?: string;
  recordingId?: string;
  transcript_id?: string;
  transcriptId?: string;
  error?: string;
  message?: string;
  updated_at?: string;
  updatedAt?: string;
  [key: string]: any;
}

interface ApiContextType {
  API_URL: string;
  getAuthHeaders: () => Record<string, string>;

  // Generic HTTP methods
  post: (endpoint: string, data: any, options?: { suppressBanner?: boolean; successMessage?: string }) => Promise<any>;
  get: (endpoint: string, options?: { responseType?: 'json' | 'blob'; suppressBanner?: boolean; successMessage?: string }) => Promise<any>;
  put: (endpoint: string, data?: any, options?: { suppressBanner?: boolean; successMessage?: string }) => Promise<any>;
  delete: (endpoint: string, data?: any, options?: { suppressBanner?: boolean; successMessage?: string }) => Promise<any>;

  // Auth
  login: (username: string, password: string) => Promise<any>;
  logout: () => Promise<any>;
  resetPassword: (username: string, newPassword: string) => Promise<any>;
  
  // User Profile
  getUserProfile: () => Promise<any>;
  updateUserProfile: (profileData: any) => Promise<any>;
  getUserEmails: () => Promise<any>;
  getConnectedAccounts: () => Promise<any>;
  
  // User Security
  changePassword: (passwordData: { current_password: string; new_password: string }) => Promise<any>;
  getUserSessions: () => Promise<any>;
  revokeSession: (sessionId: string) => Promise<any>;
  getTwoFactorSettings: () => Promise<any>;
  enableTwoFactor: (data: { method: string }) => Promise<any>;
  disableTwoFactor: () => Promise<any>;
  generateBackupCodes: () => Promise<any>;
  
  // Projects
  getProjects: () => Promise<any[]>;
  getArchivedProjects: () => Promise<any[]>;
  createProject: (data: any) => Promise<any>;
  archiveProject: (id: string) => Promise<any>;
  unarchiveProject: (id: string) => Promise<any>;
  deleteProject: (id: string) => Promise<any>;
  batchArchiveProjects: (projectIds: string[]) => Promise<any>;
  batchUnarchiveProjects: (projectIds: string[]) => Promise<any>;
  batchDeleteProjects: (projectIds: string[]) => Promise<any>;
  copyProject: (id: string, newName?: string) => Promise<any>;
  
  // Transcripts
  getTranscripts: (projectId?: string) => Promise<any[]>;
  getTranscriptsByNavigationItem: (projectId: string, navigationItemId: string | number) => Promise<any[]>;
  updateTranscript: (id: string, data: any) => Promise<any>; // For status updates only
  archiveTranscript: (id: string) => Promise<any>;
  archiveTranscripts?: (ids: (string)[]) => Promise<any>;
  
  // Navigation
  getNavigationTree: (projectId?: string) => Promise<NavItem[]>;
  getNavigationContentFlags: (projectId: string) => Promise<Array<{ navigation_item_id: string; has_recordings: boolean; has_transcripts: boolean; has_summaries: boolean; counts?: { recordings: number; transcripts: number; summaries: number } }>>;
  createNavigationItem: (projectId: string, payload: { name: string; description?: string; item_type?: string; parent_id?: string; icon?: string; transaction_code?: string }) => Promise<any>;
  updateNavigationItem: (projectId: string, navigationItemId: string , payload: { name?: string; description?: string; transaction_code?: string }) => Promise<any>;
  deleteNavigationItem: (projectId: string, navigationItemId: string ) => Promise<{ disabled_ids?: string[] }>;
  moveNavigationItem: (projectId: string, navigationItemId: string , payload: { new_parent_id: string | null; new_display_order: number }) => Promise<any>;

  // Navigation Item Aggregated Summaries (folder-level summaries)
  getNavigationItemAggregatedSummary: (projectId: string, navigationItemId: string , options?: { version?: number; all?: boolean }) => Promise<any>;
  generateNavigationItemAggregatedSummary: (projectId: string, navigationItemId: string , triggerEvent?: string) => Promise<any>;
  updateNavigationItemAggregatedSummary: (projectId: string, navigationItemId: string , payload: { content: string; content_text?: string }) => Promise<any>;
  getDeepestNavigationItemsWithSummaries: (projectId: string) => Promise<Array<{
    id: string;
    name: string;
    item_type: string;
    parent_id: string | null;
    summary_count: number;
    latest_aggregated_version: number;
    has_aggregated_summary: boolean;
  }>>;

  // As-Is Documents
  getAsIsDocuments: (projectId: string) => Promise<any[]>;
  deleteAsIsDocument: (documentId: string) => Promise<void>;
  approveAsIsDocument: (documentId: string) => Promise<any>;
  batchApproveAsIsDocuments: (documentIds: string[]) => Promise<any>;
  archiveAsIsDocument: (documentId: string, isArchived?: boolean) => Promise<any>;
  batchArchiveAsIsDocuments: (documentIds: string[], isArchived?: boolean) => Promise<any>;
  getNodeAsIsContent: (projectId: string, navigationItemId: string) => Promise<{
    navigation_item_id: string;
    navigation_item_name: string;
    has_content: boolean;
    summary_count: number;
    aggregated_content: string;
    summaries: Array<{ id: string; title: string; status: string; created_at: string }>;
  }>;
  
  // Templates
  getNavigationTemplates: (includeArchived?: boolean) => Promise<{templates: any[], count: number}>;
  createNavigationTemplate: (data: any) => Promise<any>;
  getNavigationTemplate: (id: string, includeArchived?: boolean) => Promise<any>;
  updateNavigationTemplate: (id: string, data: any) => Promise<any>;
  archiveNavigationTemplate: (id: string) => Promise<any>;
  unarchiveNavigationTemplate: (id: string) => Promise<any>;
  downloadNavigationTemplate: (id: string, format?: 'json' | 'zip' | 'xlsx') => Promise<void>;
  downloadSampleTemplate: (format?: 'xlsx' | 'json') => Promise<void>;
  uploadNavigationTemplate: (formData: FormData) => Promise<any>;
  
  // FI-GL
  getRecordings: (projectId?: string) => Promise<any[]>;
  createRecording: (data: any) => Promise<any>;
  getCategories: () => Promise<any[]>;
  getSteps: (recordingId: string) => Promise<any[]>;
  createStep: (recordingId: string, data: any) => Promise<any>;

  // Business Process
  uploadInterviewRecording: (projectId: string, formData: FormData) => Promise<any>;
  shareInterviewRecording: (recordingId: string, shareData: { emails: string[], permissions: string[] }) => Promise<any>;
  startTranscriptionJob: (recordingId: string) => Promise<{ jobId: string; [key: string]: any }>;
  getTranscriptionJobStatus: (
    recordingId: string,
    jobId: string,
    options?: { signal?: AbortSignal }
  ) => Promise<TranscriptionJobStatusResponse>;
  getTranscriptionStatus: (recordingId: string) => Promise<any>;
  
  // Progress tracking methods
  checkTranscriptionStatus: (recordingIds: string[] | string) => Promise<any>;
  checkSummarizationStatus: (recordingIds: string[] | string, navigationItemId?: string, projectId?: string) => Promise<any>;
  
  // Process Summaries
  getProcessSummaries: (projectId?: string) => Promise<any[]>;
  getSummariesByNavigationItem: (projectId: string, navigationItemId: string) => Promise<any[]>;
  getSummaryDetail: (summaryId: string | number, projectId?: string) => Promise<any>;
  updateSummary: (summaryId: string | number, content: any, projectId?: string) => Promise<any>;
  deleteSummary: (summaryId: string | number) => Promise<any>;
  regenerateSummary: (recordingIds: string[], navigationItemId?: string, projectId?: string) => Promise<any>;
  signOffSummary: (summaryId: string | number) => Promise<any>;
  
  // Transcripts
  approveTranscripts: (transcriptIds: string[]) => Promise<any>;
  
  // Fit-Gap Documents
  getFitGapDocuments: (projectId: string) => Promise<any[]>;
  approveFitGapDocument: (documentId: string) => Promise<any>;
  batchApproveFitGapDocuments: (documentIds: string[]) => Promise<any>;
  archiveFitGapDocument: (documentId: string, isArchived?: boolean) => Promise<any>;
  batchArchiveFitGapDocuments: (documentIds: string[], isArchived?: boolean) => Promise<any>;

  // Approval Requests
  createApprovalRequest: (projectId: string, payload: {
    request_type: string;
    title: string;
    description?: string;
    entity_type: string;
    entity_id: string;
  }) => Promise<any>;

  // Global AI Defaults
  getGlobalAIDefaults: () => Promise<any>;
  updateAIProviderSettings: (providerKey: string, payload: any) => Promise<any>;

  // Project AI Settings
  getProjectAISettings: (projectId: string) => Promise<any>;
  updateProjectPromptSelection: (projectId: string, payload: { capability: string; prompt_template_id: string }) => Promise<any>;
  updateProjectPromptInstructions: (projectId: string, payload: { capability: string; instructions: string }) => Promise<any>;
  updateProjectToolToggle: (projectId: string, toolKey: string, enabled: boolean) => Promise<any>;
  updateProjectWebSearchSettings: (projectId: string, payload: { max_results: number; allow_domains_only: boolean }) => Promise<any>;
  addProjectWebSearchAllowedDomain: (projectId: string, domain: string) => Promise<any>;
  deleteProjectWebSearchAllowedDomain: (projectId: string, domain: string) => Promise<any>;
  updateProjectKnowledgeBaseSettings: (projectId: string, payload: { chunk_size: number; chunk_overlap: number; top_k: number }) => Promise<any>;
  updateProjectGuardrailTopics: (projectId: string, payload: { valid_topics: string[]; invalid_topics: string[] }) => Promise<any>;
  updateProjectAIModelSettings: (projectId: string, payload: { provider_key?: string; model_name?: string; inherit?: boolean }) => Promise<any>;

  // Suggested Questions
  getProjectSuggestedQuestions: (projectId: string) => Promise<string[]>;
  updateProjectSuggestedQuestions: (projectId: string, questions: { question: string; display_order: number }[]) => Promise<any>;
  getGlobalSuggestedQuestions: () => Promise<any[]>;
  updateGlobalSuggestedQuestions: (questions: { question: string; display_order: number }[]) => Promise<any>;

  // Prompt Templates (metadata catalog)
  getPromptTemplateCatalog: (params?: { category?: string; active_only?: boolean }) => Promise<any[]>;
  
  // Navigation Items
  getNavigationItem: (navigationItemId: string) => Promise<any>;
  
  isInitialized: boolean;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

interface AuthResponse {
  user: {
    id: number;
    username: string;
    role_id: number;
    organization_id: number;
  };
  organization: {
    id: number;
    name: string;
    code: string;
    description: string;
    active: boolean;
  };
  token: string;
}

/**
 * Handles api provider behavior.
 */
export function ApiProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, logout: authLogout } = useAuth();
  const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8082/api';

  const getAuthHeaders = (includeContentType = true) => {
    const headers: Record<string, string> = {
      ...(includeContentType ? { 'Content-Type': 'application/json' } : {}),
    };
    
    // Add Authorization header with bearer token from sessionStorage
    if (typeof window !== 'undefined') {
      const token = sessionStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    return headers;
  };

    
  // Helper: handle unauthorized responses (expired or invalid token)
  const handleUnauthorized = async (response: Response) => {
    if (response.status !== 401) return false;
    let message = 'Your session has expired. Please log in again.';
    try {
      const text = await response.text();
      if (text) message = text;
    } catch {}
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('app:error', {
        detail: {
          title: 'Session expired',
          message,
          type: 'error',
          duration: 6000,
        }
      }));
    }
    // Clear auth and redirect to login
    try { authLogout(); } catch {}
    return true;
  };

  // Auth APIs
  const login = async (username: string, password: string) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Login failed');
      }
      
      const data = await response.json();
      
      // Ensure organization data is present
      if (!data.organization) {
        throw new Error('Organization data missing from login response');
      }
      
      return data;
    } finally {
      stopLoading();
    }
  };

  const logout = async () => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/logout`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Logout failed');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const resetPassword = async (username: string, newPassword: string) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, new_password: newPassword }),
      });
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Password reset failed');
      }
      
      return response.json();
    } finally {
      stopLoading();
    }
  };

  // User Profile APIs
  const getUserProfile = async () => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/profile`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (await handleUnauthorized(response)) {
        throw new Error('Session expired');
      }
      if (!response.ok) throw new Error('Failed to fetch user profile');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const updateUserProfile = async (profileData: any) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(profileData)
      });
      if (!response.ok) throw new Error('Failed to update user profile');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const getUserEmails = async () => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/emails`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (await handleUnauthorized(response)) {
        throw new Error('Session expired');
      }
      if (!response.ok) throw new Error('Failed to fetch user emails');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const getConnectedAccounts = async () => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/connected-accounts`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (await handleUnauthorized(response)) {
        throw new Error('Session expired');
      }
      if (!response.ok) throw new Error('Failed to fetch connected accounts');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  // User Security APIs
  const changePassword = async (passwordData: { current_password: string; new_password: string }) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/password`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(passwordData)
      });
      if (!response.ok) throw new Error('Failed to change password');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const getUserSessions = async () => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/sessions`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (await handleUnauthorized(response)) {
        throw new Error('Session expired');
      }
      if (!response.ok) throw new Error('Failed to fetch user sessions');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const revokeSession = async (sessionId: string) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to revoke session');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const getTwoFactorSettings = async () => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/two-factor`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (await handleUnauthorized(response)) {
        throw new Error('Session expired');
      }
      if (!response.ok) throw new Error('Failed to fetch 2FA settings');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const enableTwoFactor = async (data: { method: string }) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/two-factor`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to enable 2FA');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const disableTwoFactor = async () => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/two-factor`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to disable 2FA');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const generateBackupCodes = async () => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/user/two-factor/backup-codes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({})
      });
      if (!response.ok) throw new Error('Failed to generate backup codes');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  // Generic HTTP methods
  const get = async (endpoint: string, options: { responseType?: 'json' | 'blob', suppressBanner?: boolean, successMessage?: string } = {}) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include', // Send cookies with request
      });

      if (!response.ok) {
        if (await handleUnauthorized(response)) {
          throw new Error('Unauthorized');
        }
        const error = await response.text();
        if (!(options as any)?.suppressBanner) {
          if (typeof document !== 'undefined') {
            document.dispatchEvent(new CustomEvent('app:error', { detail: { title: `Request failed (${response.status})`, message: error || 'Request failed', type: 'error', duration: 6000 } }));
          }
        }
        throw new Error(error || 'Request failed');
      }

      const result = options.responseType === 'blob' ? await response.blob() : await response.json();
      if (options.successMessage && typeof document !== 'undefined') {
        try { document.dispatchEvent(new CustomEvent('app:success', { detail: { message: options.successMessage } })); } catch {}
      }
      return result;
    } finally {
      stopLoading();
    }
  };

  const post = async (endpoint: string, data: any, options?: { suppressBanner?: boolean; successMessage?: string }) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include', // Send cookies with request
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        if (await handleUnauthorized(response)) {
          throw new Error('Unauthorized');
        }
        const errorText = await response.text();
        if (!options?.suppressBanner) {
          if (typeof document !== 'undefined') {
            document.dispatchEvent(new CustomEvent('app:error', { detail: { title: `Request failed (${response.status})`, message: errorText || 'Request failed', type: 'error', duration: 6000 } }));
          }
        }
        const err = new Error(errorText || 'Request failed');
        (err as any).status = response.status;
        throw err;
      }
      const result = await response.json();
      if (options?.successMessage && typeof document !== 'undefined') {
        try { document.dispatchEvent(new CustomEvent('app:success', { detail: { message: options.successMessage } })); } catch {}
      }
      return result;
    } finally {
      stopLoading();
    }
  };

  const put = async (endpoint: string, data?: any, options?: { suppressBanner?: boolean; successMessage?: string }) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include', // Send cookies with request
        body: data ? JSON.stringify(data) : undefined
      });
      if (!response.ok) {
        if (await handleUnauthorized(response)) {
          throw new Error('Unauthorized');
        }
        const errorText = await response.text();
        if (!options?.suppressBanner) {
          if (typeof document !== 'undefined') {
            document.dispatchEvent(new CustomEvent('app:error', { detail: { title: `Request failed (${response.status})`, message: errorText || 'Request failed', type: 'error', duration: 6000 } }));
          }
        }
        throw new Error(errorText || 'Request failed');
      }
      const result = await response.json();
      if (options?.successMessage && typeof document !== 'undefined') {
        try { document.dispatchEvent(new CustomEvent('app:success', { detail: { message: options.successMessage } })); } catch {}
      }
      return result;
    } finally {
      stopLoading();
    }
  };

  const del = async (endpoint: string, data?: any, options: { suppressBanner?: boolean; successMessage?: string } = {}) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include', // Send cookies with request
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response);
        }
        throw new Error(`DELETE ${endpoint} failed: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      const result = contentType && contentType.includes('application/json') ? await response.json() : await response.text();

      if (options.successMessage) {
        try {
          if (typeof document !== 'undefined') {
            document.dispatchEvent(new CustomEvent('app:success', { detail: { message: options.successMessage } }));
          }
        } catch {}
      }

      return result;
    } catch (error) {
      console.error('API DELETE error:', error);
      throw error;
    } finally {
      stopLoading();
    }
  };

  // Project AI Settings
  const getProjectAISettings = async (projectId: string) => {
    return await get(`/workflow/v2/projects/${projectId}/ai/settings`, { suppressBanner: true });
  };

  const updateProjectPromptSelection = async (
    projectId: string,
    payload: { capability: string; prompt_template_id: string }
  ) => {
    return await put(`/workflow/v2/projects/${projectId}/ai/settings/prompt`, payload, {
      successMessage: 'Prompt setting updated',
    });
  };

  const updateProjectPromptInstructions = async (
    projectId: string,
    payload: { capability: string; instructions: string }
  ) => {
    return await put(`/workflow/v2/projects/${projectId}/ai/settings/prompt-instructions`, payload, {
      successMessage: 'Prompt instructions updated',
    });
  };

  const updateProjectToolToggle = async (projectId: string, toolKey: string, enabled: boolean) => {
    return await put(
      `/workflow/v2/projects/${projectId}/ai/settings/tools/${toolKey}`,
      { tool_key: toolKey, enabled },
      { successMessage: 'Tool setting updated' }
    );
  };

  const updateProjectWebSearchSettings = async (
    projectId: string,
    payload: { max_results: number; allow_domains_only: boolean }
  ) => {
    return await put(`/workflow/v2/projects/${projectId}/ai/settings/web-search`, payload, {
      successMessage: 'Web search settings updated',
    });
  };

  const addProjectWebSearchAllowedDomain = async (projectId: string, domain: string) => {
    return await post(
      `/workflow/v2/projects/${projectId}/ai/settings/web-search/domains`,
      { domain },
      { successMessage: 'Domain added' }
    );
  };

  const deleteProjectWebSearchAllowedDomain = async (projectId: string, domain: string) => {
    const encoded = encodeURIComponent(domain);
    return await del(
      `/workflow/v2/projects/${projectId}/ai/settings/web-search/domains?domain=${encoded}`,
      undefined,
      { successMessage: 'Domain removed' }
    );
  };

  const updateProjectKnowledgeBaseSettings = async (
    projectId: string,
    payload: { chunk_size: number; chunk_overlap: number; top_k: number }
  ) => {
    return await put(`/workflow/v2/projects/${projectId}/ai/settings/knowledge-base`, payload, {
      successMessage: 'Knowledge base settings updated',
    });
  };

  const updateProjectGuardrailTopics = async (
    projectId: string,
    payload: { valid_topics: string[]; invalid_topics: string[] }
  ) => {
    return await put(`/workflow/v2/projects/${projectId}/ai/settings/guardrails`, payload, {
      successMessage: 'Guardrail topics updated',
    });
  };

  const updateProjectAIModelSettings = async (
    projectId: string,
    payload: { provider_key?: string; model_name?: string; inherit?: boolean }
  ) => {
    return await put(`/workflow/v2/projects/${projectId}/ai/settings/model`, payload, {
      successMessage: 'AI model setting updated',
    });
  };

  // Suggested Questions
  const getProjectSuggestedQuestions = async (projectId: string): Promise<string[]> => {
    return await get(`/workflow/v2/projects/${projectId}/suggested-questions`, { suppressBanner: true });
  };

  const updateProjectSuggestedQuestions = async (
    projectId: string,
    questions: { question: string; display_order: number }[]
  ) => {
    return await put(`/workflow/v2/projects/${projectId}/suggested-questions`, { questions }, {
      successMessage: 'Suggested questions updated',
    });
  };

  const getGlobalSuggestedQuestions = async () => {
    return await get(`/admin/ai/suggested-questions`, { suppressBanner: true });
  };

  const updateGlobalSuggestedQuestions = async (
    questions: { question: string; display_order: number }[]
  ) => {
    return await put(`/admin/ai/suggested-questions`, { questions }, {
      successMessage: 'Global suggested questions updated',
    });
  };

  // Global AI Defaults
  const getGlobalAIDefaults = async () => {
    return await get(`/admin/ai/defaults`, { suppressBanner: true });
  };

  const updateAIProviderSettings = async (providerKey: string, payload: any) => {
    return await put(`/admin/ai/providers/${providerKey}`, payload, {
      successMessage: 'AI provider settings updated',
    });
  };

  const getPromptTemplateCatalog = async (params?: { category?: string; active_only?: boolean }) => {
    const sp = new URLSearchParams();
    if (params?.category) sp.set('category', params.category);
    if (params?.active_only) sp.set('active_only', 'true');
    const qs = sp.toString();
    const endpoint = `/admin/prompt-templates/catalog${qs ? `?${qs}` : ''}`;
    return await get(endpoint, { suppressBanner: true });
  };

  // As-Is: list documents for a project
  const getAsIsDocuments = async (projectId: string) => {
    try {
      const endpoint = `/documents/history/${projectId}?type=as-is&includeArchived=true`;
      const response = await get(endpoint, { suppressBanner: true });
      if (Array.isArray(response)) {
        return response;
      }
      return [];
    } catch (error) {
      console.warn('getAsIsDocuments failed, returning empty list:', error);
      return [];
    }
  };

  // As-Is: delete a document by id
  const deleteAsIsDocument = async (documentId: string) => {
    try {
      await del(`/documents/${documentId}`);
    } catch (error) {
      console.error('Error deleting As-Is document:', error);
      throw error;
    }
  };

  // As-Is: approve a document by id
  const approveAsIsDocument = async (documentId: string) => {
    try {
      return await post(`/documents/as-is/${documentId}/approve`, {}, { successMessage: 'As-Is document approved successfully' });
    } catch (error) {
      console.error('Error approving As-Is document:', error);
      throw error;
    }
  };

  // As-Is: batch approve multiple documents
  const batchApproveAsIsDocuments = async (documentIds: string[]) => {
    try {
      return await post(`/documents/as-is/batch/approve`, { document_ids: documentIds }, { successMessage: `${documentIds.length} As-Is document(s) approved successfully` });
    } catch (error) {
      console.error('Error batch approving As-Is documents:', error);
      throw error;
    }
  };

  // As-Is: archive/unarchive a document by id
  const archiveAsIsDocument = async (documentId: string, isArchived: boolean = true) => {
    try {
      const action = isArchived ? 'archived' : 'unarchived';
      return await post(`/documents/as-is/${documentId}/archive`, { is_archived: isArchived }, { suppressBanner: true, successMessage: `As-Is document ${action}` });
    } catch (error) {
      console.error('Error updating As-Is document archive status:', error);
      throw error;
    }
  };

  // As-Is: batch archive/unarchive multiple documents
  const batchArchiveAsIsDocuments = async (documentIds: string[], isArchived: boolean = true) => {
    try {
      const action = isArchived ? 'archived' : 'unarchived';
      return await post(`/documents/as-is/batch/archive`, { document_ids: documentIds, is_archived: isArchived }, { successMessage: `${documentIds.length} As-Is document(s) ${action}` });
    } catch (error) {
      console.error('Error batch archiving As-Is documents:', error);
      throw error;
    }
  };

  // As-Is: get aggregated content for a navigation node (deterministic, no document creation)
  const getNodeAsIsContent = async (projectId: string, navigationItemId: string | number) => {
    try {
      const endpoint = `/workflow/v2/projects/${projectId}/navigation/${navigationItemId}/as-is-content`;
      const response = await get(endpoint, { suppressBanner: true });
      return response;
    } catch (error) {
      console.error('Error fetching node AS-IS content:', error);
      // Return empty response structure to prevent UI crashes
      return {
        navigation_item_id: String(navigationItemId),
        navigation_item_name: '',
        has_content: false,
        summary_count: 0,
        aggregated_content: '',
        summaries: []
      };
    }
  };

  // Project APIs
  const getProjects = async () => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/workflow/v2/projects`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (await handleUnauthorized(response)) {
        throw new Error('Session expired');
      }
      if (!response.ok) throw new Error('Failed to fetch projects');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const getArchivedProjects = async () => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/workflow/v2/projects?archived=true`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (await handleUnauthorized(response)) {
        throw new Error('Session expired');
      }
      if (!response.ok) throw new Error('Failed to fetch archived projects');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  const createProject = async (data: any) => {
    startLoading();
    try {
      const response = await fetch(`${API_URL}/workflow/v2/projects`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create project');
      return response.json();
    } finally {
      stopLoading();
    }
  };

  // Transcript APIs
  const getTranscripts = async (projectId?: string | number) => {
    try {
      // Determine the endpoint based on whether projectId is provided
      const endpoint = projectId 
        ? `/workflow/v2/projects/${projectId}/transcripts`
        : '/transcripts';
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn(`No transcripts found for project ${projectId}`);
          return [];
        }
        throw new Error(`Failed to fetch transcripts: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle different response formats safely
      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray((data as any).transcripts)) {
        return (data as any).transcripts;
      } else if (data && (data as any).data && Array.isArray((data as any).data)) {
        return (data as any).data;
      } else {
        console.warn('Unexpected or empty response format for transcripts:', data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching transcripts:', error);
      return [];
    }
  };
  
  // Get transcripts by navigation item
  const getTranscriptsByNavigationItem = async (projectId: string, navigationItemId: string | number) => {
    try {
      const endpoint = `/workflow/v2/projects/${projectId}/navigation-items/${navigationItemId}/transcripts`;
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (!response.ok) {
        if (await handleUnauthorized(response)) {
          throw new Error('Unauthorized');
        }
        if (response.status === 404) {
          console.warn(`No transcripts found for navigation item ${navigationItemId} in project ${projectId}`);
          return [];
        }
        throw new Error(`Failed to fetch transcripts for navigation item: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle different response formats safely
      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray((data as any).transcripts)) {
        return (data as any).transcripts;
      } else if (data && (data as any).data && Array.isArray((data as any).data)) {
        return (data as any).data;
      } else {
        console.warn('Unexpected or empty response format for navigation item transcripts:', data);
        return [];
      }
    } catch (error) {
      console.error(`Error fetching transcripts for navigation item ${navigationItemId}:`, error);
      return [];
    }
  };

  // Transcript creation removed - use workflow v2 endpoints or recording-based creation

  // Use getTranscriptsByNavigationItem or getTranscripts with projectId instead

  const updateTranscript = async (id: string, data: any) => {
    // Use the old endpoint for now until we migrate to workflow v2
    const targetUrl = `${API_URL}/transcripts/${id}`;
    const response = await fetch(targetUrl, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    const requestId = response.headers.get('x-request-id');
    if (!response.ok) {
      const errorText = await response.text();
      const baseMessage = errorText || 'Failed to update transcript';
      const message = requestId ? `${baseMessage} (requestId: ${requestId})` : baseMessage;
      throw new Error(message);
    }

    let result: any = null;
    if (response.status !== 204) {
      try {
        result = await response.json();
      } catch {
        result = null;
      }
    }

    return result ?? { id, ...data };
  };

  // Transcript deletion removed - implement via workflow v2 if needed

  // Transcript sharing/AI features removed - implement via workflow v2 if needed

  const buildTreeFromProjectNavigationItems = (items: any[]): NavItem[] => {
    if (!Array.isArray(items)) {
      return [];
    }

    const nodeMap = new Map<string, NavItem>();
    const parentLookup = new Map<string, string | null>();
    const orderLookup = new Map<string, number>();

    items.forEach((entry) => {
      if (!entry || entry.is_enabled === false) {
        return;
      }

      const raw = entry.navigation_item || entry;
      if (!raw || !raw.id) {
        return;
      }

      const id = String(raw.id);
      const normalized: NavItem = nodeMap.get(id) || {
        id: id,
        name: raw.name || `Navigation Item ${id}`,
        description: raw.description || '',
        transaction_code: raw.transaction_code || undefined,
        item_type: (raw.item_type as NavItem['item_type']) || 'folder',
        children: [],
      };

      normalized.name = raw.name || normalized.name;
      normalized.description = raw.description || '';
      normalized.transaction_code = raw.transaction_code || undefined;
      normalized.item_type = (raw.item_type as NavItem['item_type']) || normalized.item_type;
      normalized.parent_id = raw.parent_id ? String(raw.parent_id) : null;
      normalized.display_order =
        typeof raw.display_order === 'number'
          ? raw.display_order
          : typeof entry.display_order === 'number'
          ? entry.display_order
          : normalized.display_order || 0;
      normalized.icon = raw.icon || null;
      normalized.children = normalized.children || [];

      nodeMap.set(id, normalized);
      parentLookup.set(id, normalized.parent_id ?? null);
      orderLookup.set(id, normalized.display_order ?? 0);
    });

    const roots: NavItem[] = [];

    nodeMap.forEach((node, id) => {
      const parentId = parentLookup.get(id);
      if (parentId && nodeMap.has(parentId)) {
        const parent = nodeMap.get(parentId)!;
        parent.children = parent.children || [];
        if (!parent.children.some((child) => child.id === id)) {
          parent.children.push(node);
        }
      } else {
        roots.push(node);
      }
    });

    const sortTree = (nodes: NavItem[]) => {
      nodes.sort((a, b) => {
        const orderA = orderLookup.get(a.id) ?? 0;
        const orderB = orderLookup.get(b.id) ?? 0;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        return a.name.localeCompare(b.name);
      });
      nodes.forEach((child) => {
        if (child.children && child.children.length > 0) {
          sortTree(child.children);
        }
      });
    };

    sortTree(roots);
    return roots;
  };

  // Navigation APIs
  const getNavigationTree = async (projectId?: string | number) => {
    if (projectId) {
      try {
        const projectNavResponse = await fetch(
          `${API_URL}/workflow/projects/${projectId}/navigation-items`,
          {
            method: 'GET',
            headers: getAuthHeaders(),
          }
        );

        if (projectNavResponse.ok) {
          const rawItems = await projectNavResponse.json();
          const normalized = buildTreeFromProjectNavigationItems(rawItems);
          if (normalized.length > 0) {
            return normalized;
          }
        } else {
          if (await handleUnauthorized(projectNavResponse)) return [];
          const txt = await projectNavResponse.text();
          console.warn(
            'Project navigation items endpoint failed, falling back to tree endpoint:',
            projectNavResponse.status,
            txt
          );
        }
      } catch (err) {
        console.warn('Failed to fetch project navigation items, falling back to tree endpoint', err);
      }
    }

    try {
      const endpoint = projectId ? `/navigation/tree?project_id=${projectId}` : '/navigation/tree';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (await handleUnauthorized(response)) return [];
        const errorText = await response.text();
        console.error('Failed to fetch navigation tree:', errorText);
        throw new Error(errorText || 'Failed to fetch navigation tree');
      }
      const data = await response.json();
      return Array.isArray(data) ? data : data?.data || [];
    } catch (error) {
      console.error('Error fetching navigation tree:', error);
      return [];
    }
  };

  const getNavigationContentFlags = async (projectId: string) => {
    try {
      const endpoint = `/workflow/v2/projects/${projectId}/navigation-items/content-flags`;
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        if (await handleUnauthorized(response)) return [] as any[];
        const txt = await response.text();
        console.error('Failed to fetch navigation content flags:', response.status, txt);
        return [] as any[];
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.warn('Error fetching navigation content flags', err);
      return [] as any[];
    }
  };

  const createNavigationItem = async (
    projectId: string,
    payload: { name: string; description?: string; item_type?: string; parent_id?: string; icon?: string; transaction_code?: string }
  ) => {
    const endpoint = `/workflow/projects/${projectId}/navigation-items`;
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (await handleUnauthorized(response)) throw new Error('Unauthorized');
      const txt = await response.text();
      throw new Error(txt || 'Failed to create navigation item');
    }
    return response.json();
  };

  const updateNavigationItemAggregatedSummary = async (
    projectId: string,
    navigationItemId: string | number,
    payload: { content: string; content_text?: string }
  ) => {
    const endpoint = `/workflow/v2/projects/${projectId}/navigation-items/${navigationItemId}/aggregated-summary`;
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (await handleUnauthorized(response)) throw new Error('Unauthorized');
      const txt = await response.text();
      throw new Error(txt || 'Failed to update aggregated summary');
    }
    return response.json();
  };

  const updateNavigationItem = async (
    projectId: string,
    navigationItemId: string | number,
    payload: { name?: string; description?: string; transaction_code?: string }
  ) => {
    const endpoint = `/workflow/projects/${projectId}/navigation-items/${navigationItemId}`;
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (await handleUnauthorized(response)) throw new Error('Unauthorized');
      const txt = await response.text();
      throw new Error(txt || 'Failed to update navigation item');
    }
    return response.json();
  };

  const deleteNavigationItem = async (projectId: string, navigationItemId: string | number) => {
    const endpoint = `/workflow/projects/${projectId}/navigation-items/${navigationItemId}`;
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (await handleUnauthorized(response)) throw new Error('Unauthorized');
      const txt = await response.text();
      throw new Error(txt || 'Failed to delete navigation item');
    }
    const data = await response.text();
    if (!data) return {};
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  };

  const moveNavigationItem = async (
    projectId: string,
    navigationItemId: string | number,
    payload: { new_parent_id: string | null; new_display_order: number }
  ) => {
    const endpoint = `/workflow/projects/${projectId}/navigation-items/${navigationItemId}/move`;
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      if (await handleUnauthorized(response)) throw new Error('Unauthorized');
      const txt = await response.text();
      throw new Error(txt || 'Failed to move navigation item');
    }
    return response.json();
  };

  // Navigation Item Aggregated Summary APIs (folder-level summaries)
  const getNavigationItemAggregatedSummary = async (
    projectId: string,
    navigationItemId: string | number,
    options?: { version?: number; all?: boolean }
  ) => {
    let endpoint = `/workflow/v2/projects/${projectId}/navigation-items/${navigationItemId}/aggregated-summary`;
    const params = new URLSearchParams();
    if (options?.version) params.append('version', String(options.version));
    if (options?.all) params.append('all', 'true');
    if (params.toString()) endpoint += `?${params.toString()}`;

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        return null; // Silently return null — caller falls back to generate endpoint
      }
      return response.json();
    } catch {
      return null;
    }
  };

  const generateNavigationItemAggregatedSummary = async (
    projectId: string,
    navigationItemId: string | number,
    triggerEvent: string = 'generate_as_is'
  ) => {
    const endpoint = `/workflow/v2/projects/${projectId}/navigation-items/${navigationItemId}/aggregated-summary/generate`;
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ navigation_item_id: Number(navigationItemId), trigger_event: triggerEvent }),
    });
    if (!response.ok) {
      if (await handleUnauthorized(response)) throw new Error('Unauthorized');
      const txt = await response.text();
      throw new Error(txt || 'Failed to generate aggregated summary');
    }
    return response.json();
  };

  const getDeepestNavigationItemsWithSummaries = async (projectId: string) => {
    const endpoint = `/workflow/v2/projects/${projectId}/navigation-items/deepest-with-summaries`;
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      if (await handleUnauthorized(response)) throw new Error('Unauthorized');
      const txt = await response.text();
      throw new Error(txt || 'Failed to fetch deepest navigation items');
    }
    return response.json();
  };

  // Get a single navigation item by ID
  // TODO: Backend endpoint needs to be implemented
  const getNavigationItem = async (navigationItemId: string | number) => {
    try {
      const url = `${API_URL}/navigation/items/${navigationItemId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        // If endpoint doesn't exist (404), return placeholder data gracefully
        if (response.status === 404) {
          console.warn(`Navigation item endpoint not implemented yet for item ${navigationItemId}`);
          return {
            id: navigationItemId,
            name: `Navigation Item ${navigationItemId}`,
            description: "SAP Transaction/Process",
            icon: "folder",
            item_type: "component"
          };
        }
        throw new Error(`Failed to fetch navigation item: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching navigation item:', error);
      // Return placeholder data instead of throwing to prevent UI crashes
      console.warn('Returning placeholder navigation item due to missing backend endpoint');
      return {
        id: navigationItemId,
        name: `Navigation Item ${navigationItemId}`,
        description: "SAP Transaction/Process",
        icon: "folder",
        item_type: "component"
      };
    }
  };

  // FI-GL APIs
  const getRecordings = async (projectId?: string | number) => {
    try {
      // If no project ID is provided, return an empty array
      if (!projectId) {
        console.warn('No project ID provided to getRecordings, returning empty array');
        return [];
      }

      // Use the v2 project-specific endpoint which is more reliable
      const endpoint = `${API_URL}/workflow/v2/projects/${projectId}/recordings`;
      
      console.log('Fetching recordings from:', endpoint);
      const response = await fetch(endpoint, {
        headers: getAuthHeaders(),
        credentials: 'include',
        mode: 'cors'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to fetch recordings:', response.status, errorText);
        throw new Error(`Failed to fetch recordings: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      console.log('Fetched recordings:', data);
      return data;
    } catch (error) {
      console.error('Error fetching recordings:', error);
      throw error;
    }
  };

  const createRecording = async (data: any) => {
    const response = await fetch(`${API_URL}/figl/recordings`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create recording');
    return response.json();
  };

  const getCategories = async () => {
    const response = await fetch(`${API_URL}/figl/categories`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch categories');
    return response.json();
  };

  const getSteps = async (recordingId: string) => {
    const response = await fetch(`${API_URL}/figl/recordings/${recordingId}/steps`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch steps');
    return response.json();
  };

  const createStep = async (recordingId: string, data: any) => {
    const response = await fetch(`${API_URL}/figl/recordings/${recordingId}/steps`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create step');
    return response.json();
  };

  // Interview Recording APIs
  const uploadInterviewRecording = async (projectId: string, formData: FormData) => {
    try {
      const audio = formData.get('audio');
      if (!(audio instanceof Blob) || audio.size === 0) {
        throw new Error('Audio file is required');
      }

      const metadataRaw = formData.get('metadata');
      const metadata = typeof metadataRaw === 'string' && metadataRaw.trim()
        ? JSON.parse(metadataRaw)
        : {};

      const navigationItemId = String(metadata.navigation_item_id || '').trim();
      if (!navigationItemId) {
        throw new Error('navigation_item_id is required');
      }

      const filename = audio instanceof File && audio.name
        ? audio.name
        : `recording_${Date.now()}.webm`;
      const contentType = audio.type || 'application/octet-stream';
      const title = typeof metadata.title === 'string' ? metadata.title : '';
      const description = typeof metadata.description === 'string' ? metadata.description : '';
      const tags = Array.isArray(metadata.tags) ? metadata.tags : [];
      const durationSeconds = Number(metadata.duration_seconds ?? metadata.duration);

      console.log('Starting multipart recording upload:', {
        projectId,
        filename,
        size: audio.size,
        contentType,
      });

      const init = await initiateMultipartRecordingUpload(projectId, {
        navigation_item_id: navigationItemId,
        filename,
        content_type: contentType,
        title,
        description,
        tags,
      });

      const partSize = 8 * 1024 * 1024;
      const totalParts = Math.ceil(audio.size / partSize);
      const parts: { part_number: number; etag: string }[] = [];

      try {
        for (let index = 0; index < totalParts; index++) {
          const partNumber = index + 1;
          const start = index * partSize;
          const end = Math.min(start + partSize, audio.size);
          const partBlob = audio.slice(start, end, contentType);

          const { url } = await presignMultipartRecordingPart(projectId, init.upload_id, {
            key: init.key,
            part_number: partNumber,
          });

          const putResponse = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': contentType },
            body: partBlob,
          });

          if (!putResponse.ok) {
            throw new Error(`Failed to upload part ${partNumber}: ${putResponse.status}`);
          }

          const etag = putResponse.headers.get('ETag') || putResponse.headers.get('etag');
          if (!etag) {
            throw new Error(`Missing ETag for uploaded part ${partNumber}`);
          }
          parts.push({ part_number: partNumber, etag: etag.replace(/"/g, '') });
        }
      } catch (uploadError) {
        try {
          await abortMultipartRecordingUpload(projectId, {
            upload_id: init.upload_id,
            key: init.key,
          });
        } catch (abortError) {
          console.warn('Failed to abort multipart recording upload:', abortError);
        }
        throw uploadError;
      }

      const result = await completeMultipartRecordingUpload(projectId, {
        upload_id: init.upload_id,
        key: init.key,
        parts,
        navigation_item_id: navigationItemId,
        title,
        description,
        ...(Number.isFinite(durationSeconds) && durationSeconds > 0
          ? { duration_seconds: Math.round(durationSeconds) }
          : {}),
        tags,
        content_type: contentType,
      });

      console.log('Upload successful:', result);
      try { if (typeof document !== 'undefined') { document.dispatchEvent(new CustomEvent('app:success', { detail: { message: 'Recording uploaded successfully' } })); } } catch {}
      return result;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

// Multipart upload APIs
const initiateMultipartRecordingUpload = async (
  projectId: string,
  payload: {
    navigation_item_id: string;
    filename: string;
    content_type: string;
    title?: string;
    description?: string;
    tags?: string[];
  }
) => {
  const res = await fetch(`${API_URL}/workflow/v2/projects/${projectId}/recordings/uploads/initiate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      ...payload,
      navigation_item_id: String(payload.navigation_item_id),
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { upload_id, key, ... }
};

const presignMultipartRecordingPart = async (
  projectId: string,
  uploadId: string,
  body: { key: string; part_number: number; expires_seconds?: number }
) => {
  const res = await fetch(`${API_URL}/workflow/v2/projects/${projectId}/recordings/uploads/${uploadId}/parts`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { url }
};

const completeMultipartRecordingUpload = async (
  projectId: string,
  payload: {
    upload_id: string;
    key: string;
    parts: { part_number: number; etag: string }[];
    navigation_item_id: string;
    title?: string;
    description?: string;
    duration_seconds?: number;
    tags?: string[];
    content_type?: string;
  }
) => {
  const res = await fetch(`${API_URL}/workflow/v2/projects/${projectId}/recordings/uploads/${payload.upload_id}/complete`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      ...payload,
      navigation_item_id: String(payload.navigation_item_id),
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json(); // { success, data: recording }
};

const abortMultipartRecordingUpload = async (
  projectId: string,
  payload: { upload_id: string; key: string }
) => {
  const res = await fetch(`${API_URL}/workflow/v2/projects/${projectId}/recordings/uploads/${payload.upload_id}/abort`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

  const shareInterviewRecording = async (recordingId: string, shareData: { emails: string[], permissions: string[] }) => {
    const response = await fetch(`${API_URL}/interviews/recordings/${recordingId}/share`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(shareData)
    });
    if (!response.ok) throw new Error('Failed to share interview recording');
    return response.json();
  };

  const startLegacyTranscriptionJob = async (recordingId: string, language?: string) => {
    const transcriptionLang = language || (typeof localStorage !== 'undefined' ? localStorage.getItem('erp_language') : null) || 'en';
    const response = await fetch(`${API_URL}/interviews/recordings/${recordingId}/transcribe`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        provider: 'runpod',
        config: { language: transcriptionLang },
      }),
    });

    const requestId = response.headers.get('x-request-id');
    const withRequestId = (message: string) => (requestId ? `${message} (requestId: ${requestId})` : message);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(withRequestId(errorText || `Failed to start legacy transcription (status ${response.status})`));
    }

    let data: any = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (data?.transcription) {
      try {
        await put(`/interviews/recordings/${recordingId}/transcript`, {
          transcript: data.transcription,
        }, { suppressBanner: true });
      } catch (error) {
        console.error('Failed to persist transcript after legacy transcription job', error);
      }
    }

    return {
      jobId: null,
      legacy: true,
      ...data,
    };
  };

  const startTranscriptionJob = async (recordingId: string) => {
    /**
     * Production API still exposes only the legacy S3 transcription endpoint.
     * Call it directly to avoid noisy 404s while keeping the same return shape
     * expected by the rest of the client code.
     */
    return startLegacyTranscriptionJob(recordingId);
  };

  const getTranscriptionJobStatus = async (
    recordingId: string,
    jobId: string,
    options: { signal?: AbortSignal } = {}
  ): Promise<TranscriptionJobStatusResponse> => {
    try {
      const response = await fetch(
        `${API_URL}/interviews/recordings/${recordingId}/transcriptions/${jobId}/status`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
          signal: options.signal,
        }
      );

      const requestId = response.headers.get('x-request-id');
      const withRequestId = (message: string) => (requestId ? `${message} (requestId: ${requestId})` : message);

      if (response.status === 404) {
        throw new Error(withRequestId('Transcription job was not found.'));
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(withRequestId(errorText || `Failed to fetch transcription status (status ${response.status})`));
      }

      const data = (await response.json()) as TranscriptionJobStatusResponse;
      return data;
    } catch (error) {
      if ((error as any)?.name === 'AbortError') {
        throw error;
      }
      console.error(`Error fetching transcription status for recording ${recordingId}, job ${jobId}:`, error);
      throw error;
    }
  };

  const transcribeInterviewRecording = async (recordingId: string) => {
    return startTranscriptionJob(recordingId);
  };

  const getTranscriptionStatus = async (recordingId: string) => {
    const response = await fetch(`${API_URL}/interviews/recordings/${recordingId}/transcription-status`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get transcription status');
    return response.json();
  };
  
  // Template APIs
  const getNavigationTemplates = async (includeArchived?: boolean) => {
    try {
      // Try to read organization_id for scoping (optional)
      let organizationId: number | null = null;
      if (typeof window !== 'undefined') {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            organizationId = user?.org_uuid ?? null;
          } catch (e) {
            console.warn('Could not parse user from localStorage for org scope:', e);
          }
        }
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (includeArchived) {
        queryParams.set('includeArchived', 'true');
      }

      const queryString = queryParams.toString();
      const path = `/workflow/v2/templates${queryString ? '?' + queryString : ''}`;

      const res = await fetch(`${API_URL}${path}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        if (res.status === 401) {
          try { await handleUnauthorized(res as any); } catch {}
          throw new Error('Unauthorized');
        }
        const txt = await res.text();
        throw new Error(`Failed to fetch navigation templates: ${res.status} ${txt}`);
      }

      const data = await res.json();
      // Normalize return shape
      if (Array.isArray(data)) {
        return { templates: data, count: data.length };
      }
      if (data && Array.isArray((data as any).templates)) {
        return { templates: (data as any).templates, count: (data as any).count ?? (data as any).templates.length };
      }
      if (data && Array.isArray((data as any).data)) {
        return { templates: (data as any).data, count: (data as any).data.length };
      }
      // Unexpected shape; return empty gracefully
      console.warn('Unexpected templates response shape; returning empty');
      return { templates: [], count: 0 };
    } catch (error) {
      // Final guard; do not throw to avoid noisy console errors in UI
      console.warn('Error fetching templates; returning empty:', error);
      return { templates: [], count: 0 };
    }
  };

  const createNavigationTemplate = async (data: any) => {
    const response = await fetch(`${API_URL}/workflow/v2/templates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create navigation template');
    return response.json();
  };
  
  const getNavigationTemplate = async (id: string, includeArchived?: boolean) => {
    const queryParams = new URLSearchParams();
    if (includeArchived) {
      queryParams.set('includeArchived', 'true');
    }
    const queryString = queryParams.toString();
    const path = `/workflow/v2/templates/${id}${queryString ? '?' + queryString : ''}`;
    const response = await fetch(`${API_URL}${path}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch navigation template');
    return response.json();
  };
  
  const updateNavigationTemplate = async (id: string, data: any) => {
    const response = await fetch(`${API_URL}/workflow/v2/templates/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to update navigation template');
    return response.json();
  };
  
  const archiveNavigationTemplate = async (id: string) => {
    const response = await fetch(`${API_URL}/workflow/v2/templates/${id}/archive`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to archive navigation template');
    return response.json();
  };
  
  const unarchiveNavigationTemplate = async (id: string) => {
    const response = await fetch(`${API_URL}/workflow/v2/templates/${id}/unarchive`, {
      method: 'PUT',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to unarchive navigation template');
    return response.json();
  };

  const downloadNavigationTemplate = async (id: string, format: 'json' | 'zip' | 'xlsx' = 'json') => {
    const response = await fetch(`${API_URL}/workflow/v2/templates/${id}/download?format=${format}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to download navigation template');
    
    // Get the filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `template.${format}`;
    if (contentDisposition) {
      const matches = contentDisposition.match(/filename="([^"]+)"/);
      if (matches) {
        filename = matches[1];
      }
    }
    
    // Get the blob and create download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadSampleTemplate = async (format: 'xlsx' | 'json' = 'xlsx') => {
    const response = await fetch(`${API_URL}/workflow/v2/templates/sample?format=${format}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to download sample template');
    
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `sample_template.${format}`;
    if (contentDisposition) {
      const matches = contentDisposition.match(/filename="([^"]+)"/);
      if (matches) {
        filename = matches[1];
      }
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const uploadNavigationTemplate = async (formData: FormData) => {
    const response = await fetch(`${API_URL}/workflow/v2/templates/upload`, {
      method: 'POST',
      headers: getAuthHeaders(false), // Don't include Content-Type for FormData
      body: formData
    });
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Upload failed');
      throw new Error(errorText);
    }
    return response.json();
  };



  // Progress tracking methods
  const checkTranscriptionStatus = async (recordingIds: string[] | string) => {
    try {
      const idsParam = Array.isArray(recordingIds) ? recordingIds.join(',') : recordingIds;
      const response = await fetch(`${API_URL}/transcription-status?recording_ids=${idsParam}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to check transcription status: ${response.status}`);
      }
      
      return response.json();
    } catch (error) {
      console.error('Error checking transcription status:', error);
      throw error;
    }
  };
  
  // Define types for summary response
  type SummaryItem = {
    text: string;
    html: string;
  };

  type SummarizationStatusResponse = {
    status: string;
    progress: number;
    message: string;
    summaries?: SummaryItem[];
    is_signed_off?: boolean;
    signed_off_by?: string | null;
    signed_off_at?: string | null;
    id?: number;
  };

  const checkSummarizationStatus = async (recordingIds: string[] | string, navigationItemId?: string, projectId?: string) => {
    try {
      // Convert recordingIds to array if it's a string
      const recordingIdsArray = Array.isArray(recordingIds) 
        ? recordingIds 
        : recordingIds.split(',').map(id => parseInt(id.trim(), 10));
      
      // Prepare request body
      const requestBody = {
        recording_ids: recordingIdsArray,
        navigation_item_id: navigationItemId ? parseInt(navigationItemId, 10) : undefined,
        project_id: projectId ? parseInt(projectId, 10) : undefined
      };
      
      const url = `${API_URL}/check-summary-status`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to check summarization status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Check summary status response:', data);
      
      // Transform the response to match the expected format from summarization-status endpoint
      // AND include the summary content if it exists
      const result: SummarizationStatusResponse = {
        status: data.exists ? 'completed' : 'needs_generation',
        progress: data.exists ? 100 : 0,
        message: data.exists ? 'Summary is ready' : 'No existing summary found. Please generate a new summary.'
      };
      
      // If summary exists, include the content in the response
      if (data.exists && data.content) {
        // Format the content to match what the frontend expects
        result.summaries = data.content.map(item => ({
          text: item.text || '',
          html: item.html || ''
        }));
        result.is_signed_off = data.is_signed_off || false;
        result.signed_off_by = data.signed_off_by || null;
        result.signed_off_at = data.signed_off_at || null;
        result.id = data.id;
      }
      
      return result;
    } catch (error) {
      console.error('Error checking summarization status:', error);
      throw error;
    }
  };

  // Get process summaries for a project
  const getProcessSummaries = async (projectId?: string | number) => {
    try {
      let url;
      if (projectId) {
        url = `${API_URL}/workflow/v2/projects/${projectId}/summaries`;
      } else {
        // Fallback to a general endpoint if no project ID is provided
        url = `${API_URL}/workflow/v2/summaries`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch process summaries: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching process summaries:', error);
      throw error;
    }
  };

  // Get summaries for a specific navigation item
  // TODO: Backend endpoint needs to be implemented
  const getSummariesByNavigationItem = async (projectId: string, navigationItemId: string | number) => {
    try {
      const url = `${API_URL}/workflow/v2/projects/${projectId}/navigation-items/${navigationItemId}/summaries`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        // If endpoint doesn't exist (404), return empty array gracefully
        if (response.status === 404) {
          console.warn(`Navigation item summaries endpoint not implemented yet for project ${projectId}, navigation item ${navigationItemId}`);
          return [];
        }
        throw new Error(`Failed to fetch navigation item summaries: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching navigation item summaries:', error);
      // Return empty array instead of throwing to prevent UI crashes
      console.warn('Returning empty summaries array due to missing backend endpoint');
      return [];
    }
  };

  // Get a single summary by ID
  const getSummaryDetail = async (summaryId: string | number, projectId?: string | number) => {
    try {
      // Use the project-scoped endpoint if projectId is provided, otherwise fall back to the old endpoint
      const url = projectId
        ? `${API_URL}/workflow/v2/projects/${projectId}/summaries/${summaryId}`
        : `${API_URL}/workflow/v2/summaries/${summaryId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch summary detail: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching summary detail:', error);
      throw error;
    }
  };

  // Update a summary's content
  const updateSummary = async (summaryId: string, content: any, projectId?: string) => {
    try {
      // Use the project-scoped endpoint if projectId is provided, otherwise fall back to the old endpoint
      const url = projectId
        ? `${API_URL}/workflow/v2/projects/${projectId}/summaries/${summaryId}`
        : `${API_URL}/workflow/v2/summaries/${summaryId}`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update summary: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating summary:', error);
      throw error;
    }
  };

  // Delete a summary by ID
  const deleteSummary = async (summaryId: string) => {
    try {
      const url = `${API_URL}/workflow/v2/summaries/${summaryId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete summary: ${response.status}`);
      }
      
      try {
        if (typeof document !== 'undefined') {
          document.dispatchEvent(new CustomEvent('app:success', { detail: { message: 'Summary deleted successfully' } }));
        }
      } catch {}
      return true;
    } catch (error) {
      console.error('Error deleting summary:', error);
      throw error;
    }
  };
  
  // Regenerate a summary from recording IDs
  const regenerateSummary = async (recordingIds: string[], navigationItemId?: string, projectId?: string) => {
    try {
      const url = `${API_URL}/generate-summary`;
      
      const requestBody = {
        recording_ids: recordingIds,
        navigation_item_id: navigationItemId,
        project_id: projectId
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to regenerate summary: ${response.status}`);
      }
      
      const result = await response.json();
      try {
        if (typeof document !== 'undefined') {
          document.dispatchEvent(new CustomEvent('app:success', { detail: { message: 'Summary regenerated successfully' } }));
        }
      } catch {}
      return result;
    } catch (error) {
      console.error('Error regenerating summary:', error);
      throw error;
    }
  };
  
  // Sign off (approve) a summary
  const signOffSummary = async (summaryId: string) => {
    try {
      const url = `${API_URL}/sign-off-summary`;
      
      const requestBody = {
        summary_id: summaryId
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to sign off summary: ${response.status}`);
      }
      
      const result = await response.json();
      try {
        if (typeof document !== 'undefined') {
          document.dispatchEvent(new CustomEvent('app:success', { detail: { message: 'Summary approved successfully' } }));
        }
      } catch {}
      return result;
    } catch (error) {
      console.error('Error signing off summary:', error);
      throw error;
    }
  };

  // Approve multiple transcripts
  const approveTranscripts = async (transcriptIds: string[]) => {
    try {
      const url = `${API_URL}/approve-transcripts`;
      
      const requestBody = {
        transcript_ids: transcriptIds
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to approve transcripts: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error approving transcripts:', error);
      throw error;
    }
  };

  // Archive a single transcript
  const archiveTranscript = async (id: string) => {
    try {
      return await post(`/transcripts/${id}/archive`, {});
    } catch (error) {
      console.error('Error archiving transcript:', error);
      throw error;
    }
  };

  // Archive multiple transcripts (if backend supports)
  const archiveTranscripts = async (ids: string[]) => {
    try {
      return await post(`/transcripts/archive`, { transcript_ids: ids });
    } catch (error) {
      console.error('Error archiving transcripts:', error);
      throw error;
    }
  };

  // Fit-Gap: list documents for a project
  const getFitGapDocuments = async (projectId: string) => {
    try {
      const endpoint = `/documents/history/${projectId}?type=fit-gap&includeArchived=true`;
      const response = await get(endpoint, { suppressBanner: true });
      if (Array.isArray(response)) {
        return response;
      }
      return [];
    } catch (error) {
      console.warn('getFitGapDocuments failed, returning empty list:', error);
      return [];
    }
  };

  // Fit-Gap: approve a document by id
  const approveFitGapDocument = async (documentId: string) => {
    try {
      return await post(`/documents/fit-gap/${documentId}/approve`, {});
    } catch (error) {
      console.error('Error approving Fit-Gap document:', error);
      throw error;
    }
  };

  // Fit-Gap: batch approve multiple documents
  const batchApproveFitGapDocuments = async (documentIds: string[]) => {
    try {
      return await post(`/documents/fit-gap/batch/approve`, { document_ids: documentIds });
    } catch (error) {
      console.error('Error batch approving Fit-Gap documents:', error);
      throw error;
    }
  };

  // Fit-Gap: archive/unarchive a document by id
  const archiveFitGapDocument = async (documentId: string, isArchived: boolean = true) => {
    try {
      const action = isArchived ? 'archived' : 'unarchived';
      return await post(`/documents/fit-gap/${documentId}/archive`, { is_archived: isArchived }, { suppressBanner: true });
    } catch (error) {
      console.error('Error updating Fit-Gap document archive status:', error);
      throw error;
    }
  };

  // Fit-Gap: batch archive/unarchive multiple documents
  const batchArchiveFitGapDocuments = async (documentIds: string[], isArchived: boolean = true) => {
    try {
      const action = isArchived ? 'archived' : 'unarchived';
      return await post(`/documents/fit-gap/batch/archive`, { document_ids: documentIds, is_archived: isArchived });
    } catch (error) {
      console.error('Error batch archiving Fit-Gap documents:', error);
      throw error;
    }
  };

  // Approval Requests: create a project-scoped approval request
  const createApprovalRequest = async (projectId: string, payload: {
    request_type: string;
    title: string;
    description?: string;
    entity_type: string;
    entity_id: string;
  }) => {
    try {
      return await post(`/workflow/v2/projects/${projectId}/approvals`, payload, { suppressBanner: true, successMessage: 'Approval request submitted' });
    } catch (err: any) {
      if (err?.status === 409) {
        if (typeof document !== 'undefined') {
          document.dispatchEvent(new CustomEvent('app:info', { detail: { title: 'Already Submitted', message: 'An approval request is already pending for this item.' } }));
        }
        return; // not a failure — swallow so callers don't show a second banner
      }
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new CustomEvent('app:error', { detail: { title: 'Request Failed', message: 'Failed to submit approval request.' } }));
      }
      throw err;
    }
  };

  const api = useMemo(() => ({
    API_URL,
    getAuthHeaders,
    getProcessSummaries,
    getSummariesByNavigationItem,
    getSummaryDetail,
    updateSummary,
    deleteSummary,
    regenerateSummary,
    signOffSummary,
    approveTranscripts,
    archiveTranscript,
    archiveTranscripts,
    
    // Fit-Gap
    getFitGapDocuments,
    approveFitGapDocument,
    batchApproveFitGapDocuments,
    archiveFitGapDocument,
    batchArchiveFitGapDocuments,

    // Approval Requests
    createApprovalRequest,

    // Project AI Settings
    getProjectAISettings,
    updateProjectPromptSelection,
    updateProjectPromptInstructions,
    updateProjectToolToggle,
    updateProjectWebSearchSettings,
    addProjectWebSearchAllowedDomain,
    deleteProjectWebSearchAllowedDomain,
    updateProjectKnowledgeBaseSettings,
    updateProjectGuardrailTopics,
    updateProjectAIModelSettings,

    // Suggested Questions
    getProjectSuggestedQuestions,
    updateProjectSuggestedQuestions,
    getGlobalSuggestedQuestions,
    updateGlobalSuggestedQuestions,

    // Global AI Defaults
    getGlobalAIDefaults,
    updateAIProviderSettings,

    // Prompt Templates
    getPromptTemplateCatalog,

    // Generic HTTP methods
    post,
    get,
    put,
    delete: del,

    // Auth
    login,
    logout,
    resetPassword,
    
    // User Profile
    getUserProfile,
    updateUserProfile,
    getUserEmails,
    getConnectedAccounts,
    
    // User Security
    changePassword,
    getUserSessions,
    revokeSession,
    getTwoFactorSettings,
    enableTwoFactor,
    disableTwoFactor,
    generateBackupCodes,
    
    // Projects
    getProjects,
    getArchivedProjects,
    createProject,
    archiveProject: (id: string) => apiArchiveProject(id, API_URL, getAuthHeaders),
    unarchiveProject: (id: string) => apiUnarchiveProject(id, API_URL, getAuthHeaders),
    deleteProject: (id: string) => apiDeleteProject(id, API_URL, getAuthHeaders),
    batchArchiveProjects: (projectIds: string[]) => apiBatchArchiveProjects(projectIds, API_URL, getAuthHeaders),
    batchUnarchiveProjects: (projectIds: string[]) => apiBatchUnarchiveProjects(projectIds, API_URL, getAuthHeaders),
    batchDeleteProjects: (projectIds: string[]) => apiBatchDeleteProjects(projectIds, API_URL, getAuthHeaders),
    copyProject: (id: string, newName?: string) => apiCopyProject(id, API_URL, getAuthHeaders, newName),
    
    // Transcripts
    getTranscripts,
    getTranscriptsByNavigationItem,
    updateTranscript, // Keep for status updates (archive/unarchive)
    
    
    // As-Is Documents
    getAsIsDocuments,
    deleteAsIsDocument,
    approveAsIsDocument,
    batchApproveAsIsDocuments,
    archiveAsIsDocument,
    batchArchiveAsIsDocuments,
    getNodeAsIsContent,
    
    // Navigation
    getNavigationTree,
    getNavigationContentFlags,
    createNavigationItem,
    updateNavigationItem,
    deleteNavigationItem,
    moveNavigationItem,
    getNavigationItem,

    // Navigation Item Aggregated Summaries
    getNavigationItemAggregatedSummary,
    generateNavigationItemAggregatedSummary,
    updateNavigationItemAggregatedSummary,
    getDeepestNavigationItemsWithSummaries,

    // Templates
    getNavigationTemplates,
    createNavigationTemplate,
    getNavigationTemplate,
    updateNavigationTemplate,
    archiveNavigationTemplate,
    unarchiveNavigationTemplate,
    downloadNavigationTemplate,
    downloadSampleTemplate,
    uploadNavigationTemplate,
    
    // FI-GL
    getRecordings,
    createRecording,
    getCategories,
    getSteps,
    createStep,

    // Business Process
    uploadInterviewRecording,
    shareInterviewRecording,
    startTranscriptionJob,
    getTranscriptionJobStatus,
    getTranscriptionStatus,
    
    // Progress tracking methods
    checkTranscriptionStatus,
    checkSummarizationStatus,
    
    // Process Summaries
    
    isInitialized: true
  }), []);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}

/**
 * Handles use api behavior.
 */
export function useApi(): ApiContextType {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
}

// Custom hook to use the API context (alias for useApi for newer components)
export function useApiContext(): ApiContextType {
  return useApi();
}
